import { useState } from 'react';
import { Button, Icon, Input, Tooltip } from '@chikara/ui';
import { CompanyInfoStepProps } from '@/features/onboarding/types/onboarding.types';
import { useValidateCustomerId } from '@/features/onboarding/hooks/useValidateCustomerId';
import styles from '../Onboarding.module.css';
import { t } from '@/i18n';


export function CompanyInfoStep({ onSuccess }: CompanyInfoStepProps) {
    const [companyId, setCompanyId] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const { validateCustomerId, isLoading, error, clearError } = useValidateCustomerId();

    const isFormValid = companyId.trim().length > 0 && acceptedTerms && !isLoading;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid) return;
        clearError();
        const validatedCustomerId = await validateCustomerId(companyId);
        if (validatedCustomerId) {
            onSuccess(validatedCustomerId);
        }
    };

    const handleCompanyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyId(e.target.value);
    };

    return (
        <form onSubmit={handleSubmit} className={styles.formView}>
            <div className={styles.stepContainer}>
                <h2 className={styles.title}>{t('onboarding.createAccount.title')}</h2>
                <p className={styles.subtitle}>{t('onboarding.createAccount.subtitle')}</p>
            </div>

            <Input
                label={t('onboarding.companyInfo.companyIdLabel')}
                value={companyId}
                onChange={handleCompanyIdChange}
                hasError={Boolean(error)}
                helpIcon={
                    <Tooltip content={t('onboarding.companyInfo.companyIdTooltip')} position="right">
                        <span className={styles.helpIconWrapper} tabIndex={0} aria-label={t('onboarding.companyInfo.companyIdTooltip')}>
                            <Icon name="help" size="sm" color="primary" />
                        </span>
                    </Tooltip>
                }
                required
            />

            {error && (
                <p className={styles.inlineError} role="alert">
                    <Icon name="warning" size="sm" color="error" />
                    <span>{error}</span>
                </p>
            )}

            <label className={styles.termsRow}>
                <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
                <span className={styles.termsText}>
                    {t('onboarding.companyInfo.acceptPrefix')}{' '}
                    <a
                        href={t('onboarding.companyInfo.privacyUrl')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.termsLink}
                    >
                        {t('onboarding.companyInfo.privacyLink')}
                    </a>{' '}
                    {t('onboarding.companyInfo.acceptConnector')}{' '}
                    <a
                        href={t('onboarding.companyInfo.termsUrl')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.termsLink}
                    >
                        {t('onboarding.companyInfo.termsLink')}
                    </a>
                </span>
            </label>

            <Button type="submit" disabled={!isFormValid} isLoading={isLoading} className={styles.submitButton}>
                {t('onboarding.action')}
            </Button>
        </form>
    );
}
